import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import WinampPlayer from '../base/WinampPlayer';
import styles from './Taskbar.module.css';
import content from '../../data/content.json';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const { taskbar } = content.global;

/**
 * Taskbar — site-wide XP taskbar pinned to the bottom of the viewport.
 *
 * Green Start button opens a Start menu with the pages as .exe/.doc entries.
 * The system tray holds the Winamp launcher and a live clock. The top Navbar
 * is untouched — this is additive chrome, not a replacement.
 *
 * Labels/items live in content.json under global.taskbar.
 */
const Taskbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const rootRef = useRef(null);
  const location = useLocation();

  // Live tray clock
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(tick);
  }, []);

  // Close the menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // While the Start menu is open, page scroll is locked (its own list still
  // scrolls internally) — scrolling the menu must never move the page
  useBodyScrollLock(menuOpen);

  // Close on outside click / Esc
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const time = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      {/* In-flow spacer so the fixed bar never covers the footer's last line */}
      <div className={styles.spacer} aria-hidden="true" />

      <div className={styles.root} ref={rootRef}>
        {/* ── Start Menu ── */}
        {menuOpen && (
          <nav className={styles.startMenu} aria-label="Start menu">
            <div className={styles.menuHeader}>
              <img
                src={`${process.env.PUBLIC_URL}/images/new logo.png`}
                alt=""
                className={styles.menuHeaderLogo}
              />
              <span>{taskbar.menu_header}</span>
            </div>

            <ul className={styles.menuList}>
              {taskbar.items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`${styles.menuItem} ${location.pathname === item.to ? styles.menuItemActive : ''}`}
                  >
                    <span className={styles.menuIcon} aria-hidden="true">{item.icon}</span>
                    {item.label}
                  </Link>
                </li>
              ))}

              <li className={styles.menuSeparator} aria-hidden="true" />

              <li>
                <a
                  href={taskbar.events.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.menuItem}
                >
                  <span className={styles.menuIcon} aria-hidden="true">{taskbar.events.icon}</span>
                  {taskbar.events.label}
                </a>
              </li>
            </ul>
          </nav>
        )}

        {/* ── Bar ── */}
        <div className={styles.bar}>
          <button
            type="button"
            className={`${styles.startBtn} ${menuOpen ? styles.startBtnOpen : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            <span className={styles.startIcon} aria-hidden="true">◈</span>
            {taskbar.start_label}
          </button>

          <div className={styles.trayFill} />

          <div className={styles.tray}>
            <WinampPlayer variant="tray" />
            <span className={styles.clock}>{time}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Taskbar;
